import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiRequest } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { appReview } from '../native/bridge';
import { usePlatform } from '../platform/platform';
import { useStartupGate } from './StartupGateCoordinator';
import { onReviewActivity, type ReviewActivity } from './reviewActivity';
import { getOverlayDepth, subscribeOverlay } from '../commands/overlayStack';
import { useTour } from '../tour/TourProvider';
import { isNativeCallModeActive } from '../native/liveCall';
import {
  REVIEW_ERROR_RECHECK_MS,
  REVIEW_NEW_SESSION_AFTER_MS,
  REVIEW_POLL_MS,
  REVIEW_SERVER_RECHECK_MS,
  REVIEW_TYPING_QUIET_MS,
  evaluateReviewMoment,
  isReviewMomentWaiting,
  runReviewPrompt,
  type ReviewPromptStatus,
  type ReviewTrigger,
} from './reviewPrompt';

/**
 * Paket A7a: kopfloser Ausloeser der automatischen Store-Bewertungsbitte.
 *
 * Ein "guter Moment" entsteht auf zwei Wegen, beide nur nach einer fehlerfreien
 * KI-Antwort auf einen ausdruecklich gesendeten eigenen Zug (Abenteuer- oder
 * Charakter-Chat, gemeldet ueber `beginReviewActivity`):
 *   - 'reply': der Nutzer liest die Antwort (Leseruhe), ohne zu tippen;
 *   - 'exit': der Nutzer verlaesst das Gespraech zu einer beliebigen anderen
 *     Seite (ausser Kauf-/Mitgliedschaftsseiten).
 * Die Regeln selbst (2 Minuten Sitzung, Ruhe nach Fehler/Dialog, 10 Minuten nach
 * Kaufseiten, 90 Tage) stehen testbar in reviewPrompt.ts; ob das Konto ueberhaupt
 * gefragt werden darf, entscheidet allein der Server. Es gibt keinen Vorab-Dialog.
 *
 * Zwei Klassen von DOM-Blockern (siehe HARD_BLOCKING_DOM / LOADING_DOM): harte
 * Stoerer (Dialog, Fehlerhinweis, Tour) starten eine 45-s-Ruhe; Ladeanzeigen
 * (Vorschlags-Platzhalter, Routen-Geruest) blockieren nur, solange sie sichtbar
 * sind, und setzen danach nur die Leseruhe neu. Im Abenteuer-Chat steht der
 * Vorschlags-Platzhalter nach fast jeder Antwort 2-20 s lang im DOM.
 */

const CHAT_ROUTE = /^\/(chat|character-chat)\/[^/]+$/;
/** Kauf- und Mitgliedschaftsflaechen: nie direkt danach fragen. */
const PURCHASE_ROUTE = /^\/(coins|mitgliedschaft|app)(\/|$)/;
const DOM_POLL_MS = 1500;
/**
 * Harte Stoerer: etwas Wichtigeres steht im Vordergrund (Dialog, Fehlerhinweis,
 * Tour). Sie blockieren, solange sie da sind, und ihr Erscheinen UND Verschwinden
 * startet die allgemeine Ruhe (REVIEW_DISTURBANCE_QUIET_MS).
 */
const HARD_BLOCKING_DOM = '[role="dialog"], [role="alertdialog"], [aria-modal="true"], dialog[open], [role="alert"], .lorath-tour-backdrop';
/**
 * Ladeanzeigen (z. B. der Platzhalter der Antwortvorschlaege, Routen-Geruest): kein
 * Stoerer, sondern nur "es passiert noch etwas". Sie blockieren, solange sie
 * sichtbar sind; ihr Ende setzt nur die Leseruhe neu (wie das Ende einer Audio-
 * Wiedergabe) und startet KEINE 45-s-Ruhe. Sonst wuerde der Vorschlags-Platzhalter,
 * der im Abenteuer-Chat nach fast jeder Antwort 2-20 s lang steht, jeden Moment
 * um 45 s verschieben oder verfallen lassen.
 */
const LOADING_DOM = '[role="status"][aria-busy="true"]';
/** Ein eingeklapptes oder verstecktes Panel zeigt dem Nutzer nichts: seine Ladeanzeige zaehlt nicht. */
const HIDDEN_ANCESTOR = '[aria-hidden="true"], [hidden], [inert]';

function storage(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}
function normalizePath(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}
function hasHardBlockingDom(): boolean {
  return document.querySelector(HARD_BLOCKING_DOM) !== null;
}
function hasLoadingDom(): boolean {
  return Array.from(document.querySelectorAll(LOADING_DOM)).some(node => node.closest(HIDDEN_ANCESTOR) === null);
}
function mediaActive(): boolean {
  if (Array.from(document.querySelectorAll('audio, video')).some(node => !(node as HTMLMediaElement).paused)) return true;
  return Boolean(window.speechSynthesis?.speaking);
}
/** Entwurf im Eingabefeld oder frisch getippt = der Nutzer schreibt gerade. */
function typingInProgress(lastTypingAt: number | null, now: number): boolean {
  const active = document.activeElement as (HTMLElement & { value?: unknown }) | null;
  const editable = active?.matches('input, textarea, select, [contenteditable="true"]') ?? false;
  if (editable) {
    const text = active?.isContentEditable ? active.textContent : active?.value;
    if (typeof text === 'string' && text.trim() !== '') return true;
  }
  return lastTypingAt !== null && now - lastTypingAt < REVIEW_TYPING_QUIET_MS;
}
const fetchStatus = () => apiRequest<ReviewPromptStatus>('/api/profile/review-prompt-status?protocol=2');

interface ChatVisit { userId: string; path: string; pending: Set<object>; lastReplyAt: number | null }
interface Armed { trigger: ReviewTrigger; userId: string; path: string; armedAt: number; since: number; lastReplyAt: number; timer: ReturnType<typeof setInterval> }
interface Tracker {
  sessionStartedAt: number;
  hiddenAt: number | null;
  lastDisturbanceAt: number | null;
  /** Letzter Kaufseiten-Besuch (oder dessen Verlassen); eigene, laengere Ruhe als ein gewoehnlicher Stoerer. */
  lastPurchaseAt: number | null;
  lastAudioEndAt: number | null;
  lastTypingAt: number | null;
  /** Ende der letzten sichtbaren Ladeanzeige: setzt nur die Leseruhe neu, ist kein Stoerer. */
  lastLoadingEndAt: number | null;
  audioActive: number;
  hardBlockerSeen: boolean;
  loadingSeen: boolean;
  previousPath: string | null;
  visit: ChatVisit | null;
  armed: Armed | null;
  running: boolean;
  deniedUntil: number;
}
function freshTracker(now: number): Tracker {
  return {
    sessionStartedAt: now, hiddenAt: null, lastDisturbanceAt: null, lastPurchaseAt: null, lastAudioEndAt: null,
    lastTypingAt: null, lastLoadingEndAt: null, audioActive: 0, hardBlockerSeen: false, loadingSeen: false,
    previousPath: null, visit: null, armed: null, running: false, deniedUntil: 0,
  };
}

export function ReviewPromptHost() {
  const { user, status } = useAuth();
  const { isNative, os } = usePlatform();
  const location = useLocation();
  const gate = useStartupGate();
  const tour = useTour();
  const userId = status === 'authed' && user?.userId ? String(user.userId) : null;
  const live = useRef({ userId, pathname: location.pathname, gate, tour: tour.active, os });
  live.current = { userId, pathname: location.pathname, gate, tour: tour.active, os };
  const tracker = useRef<Tracker>(freshTracker(Date.now()));

  const disarm = () => {
    const armed = tracker.current.armed;
    if (armed) clearInterval(armed.timer);
    tracker.current.armed = null;
  };
  const markDisturbance = () => { tracker.current.lastDisturbanceAt = Date.now(); };
  /**
   * Gleicht den DOM mit dem Tracker ab (Takt: DOM-Timer, Moment-Timer, Seitenwechsel).
   * Harte Stoerer starten beim Erscheinen und beim Verschwinden die 45-s-Ruhe;
   * Ladeanzeigen setzen beim Verschwinden nur die Leseruhe neu.
   */
  const observeDom = () => {
    const t = tracker.current;
    if (hasHardBlockingDom()) { t.hardBlockerSeen = true; markDisturbance(); }
    else if (t.hardBlockerSeen) { t.hardBlockerSeen = false; markDisturbance(); }
    if (hasLoadingDom()) t.loadingSeen = true;
    else if (t.loadingSeen) { t.loadingSeen = false; t.lastLoadingEndAt = Date.now(); }
  };

  /** Alles, was ausserhalb der Zeitregeln gegen eine Anfrage spricht — jederzeit neu auswertbar. */
  const safeNow = (): boolean => {
    const t = tracker.current;
    const l = live.current;
    const now = Date.now();
    return l.userId !== null && l.gate === 'none' && l.tour === null && t.audioActive === 0
      && !isNativeCallModeActive() && document.visibilityState === 'visible' && getOverlayDepth() === 0
      && !hasHardBlockingDom() && !hasLoadingDom() && !mediaActive() && !typingInProgress(t.lastTypingAt, now);
  };

  const fire = (armed: Armed) => {
    const t = tracker.current;
    if (t.running || Date.now() < t.deniedUntil) return;
    t.running = true;
    const startedUser = armed.userId;
    const startPath = normalizePath(live.current.pathname);
    const disturbanceAtStart = t.lastDisturbanceAt;
    // Nach jedem await: gleiche Person, gleiche Seite, nichts Neues dazwischengekommen, weiterhin ruhig.
    const stillSafe = () => live.current.userId === startedUser
      && normalizePath(live.current.pathname) === startPath
      && tracker.current.lastDisturbanceAt === disturbanceAtStart && safeNow();
    void runReviewPrompt({
      isNative: true,
      storage: storage(),
      isSafe: stillSafe,
      fetchStatus,
      claim: () => apiRequest('/api/profile/review-prompted', {
        method: 'POST',
        body: { protocol: 2, platform: live.current.os, trigger: armed.trigger },
      }),
      requestReview: () => appReview(stillSafe),
    }).then(outcome => {
      const now = Date.now();
      if (outcome === 'skipped_server') t.deniedUntil = now + REVIEW_SERVER_RECHECK_MS;
      else if (outcome === 'skipped_error') t.deniedUntil = now + REVIEW_ERROR_RECHECK_MS;
      else if (outcome === 'prompted_native') t.lastDisturbanceAt = now;
    }).catch(() => undefined).finally(() => { t.running = false; });
  };

  const tick = () => {
    const t = tracker.current;
    const armed = t.armed;
    if (!armed) return;
    observeDom();
    const now = Date.now();
    const sameUser = live.current.userId === armed.userId;
    const sameChat = armed.trigger === 'exit' || normalizePath(live.current.pathname) === armed.path;
    if (!sameUser || !sameChat) { disarm(); return; }
    const verdict = evaluateReviewMoment({
      trigger: armed.trigger,
      now,
      sessionStartedAt: t.sessionStartedAt,
      lastReplyAt: armed.lastReplyAt,
      armedAt: armed.armedAt,
      settledSince: Math.max(armed.since, t.lastAudioEndAt ?? 0, t.lastTypingAt ?? 0, t.lastLoadingEndAt ?? 0),
      lastDisturbanceAt: t.lastDisturbanceAt,
      lastPurchaseAt: t.lastPurchaseAt,
      pendingSends: armed.trigger === 'reply' ? (t.visit?.pending.size ?? 0) : 0,
    });
    if (isReviewMomentWaiting(verdict)) return;
    if (verdict === 'ok') {
      // Zeitregeln erfuellt, aber z. B. ein Entwurf liegt im Feld: weiter warten, bis das Fenster verfaellt.
      if (!safeNow()) return;
      disarm();
      fire(armed);
      return;
    }
    disarm();
  };

  const arm = (trigger: ReviewTrigger, visit: ChatVisit, lastReplyAt: number, now = Date.now()) => {
    disarm();
    const timer = setInterval(tick, REVIEW_POLL_MS);
    tracker.current.armed = { trigger, userId: visit.userId, path: visit.path, armedAt: now, since: now, lastReplyAt, timer };
  };

  // Sitzung, Stoerer und Gespraechsaktivitaet: gelten nur fuer native, angemeldete Nutzer.
  useEffect(() => {
    if (!isNative || !userId) return undefined;
    const t = freshTracker(Date.now());
    tracker.current = t;
    const onVisibility = () => {
      const now = Date.now();
      if (document.visibilityState === 'hidden') { t.hiddenAt = now; disarm(); }
      else {
        if (t.hiddenAt !== null && now - t.hiddenAt >= REVIEW_NEW_SESSION_AFTER_MS) t.sessionStartedAt = now;
        t.hiddenAt = null;
      }
      markDisturbance();
    };
    const onAudioStart = () => { t.audioActive += 1; };
    const onAudioEnd = () => { t.audioActive = Math.max(0, t.audioActive - 1); t.lastAudioEndAt = Date.now(); };
    const onTyping = () => { t.lastTypingAt = Date.now(); };
    const onActivity = (event: ReviewActivity) => {
      const visit = t.visit;
      const path = normalizePath(event.path);
      if (!visit || visit.userId !== userId || visit.path !== path || normalizePath(live.current.pathname) !== path) return;
      if (event.phase === 'start') { visit.pending.add(event.token); disarm(); return; }
      if (!visit.pending.delete(event.token)) return;
      if (event.successful) {
        visit.lastReplyAt = Date.now();
        if (visit.pending.size === 0) arm('reply', visit, visit.lastReplyAt);
      } else {
        // Ein Fehler beim Senden ist ein Stoerer; der naechste Moment braucht eine neue, fehlerfreie Antwort.
        visit.lastReplyAt = null;
        markDisturbance();
        disarm();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('tts:start', onAudioStart);
    document.addEventListener('tts:end', onAudioEnd);
    document.addEventListener('keydown', onTyping, true);
    document.addEventListener('input', onTyping, true);
    const unsubscribeOverlay = subscribeOverlay(markDisturbance);
    const unsubscribeActivity = onReviewActivity(onActivity);
    const domTimer = setInterval(observeDom, DOM_POLL_MS);
    return () => {
      disarm();
      clearInterval(domTimer);
      unsubscribeActivity();
      unsubscribeOverlay();
      document.removeEventListener('input', onTyping, true);
      document.removeEventListener('keydown', onTyping, true);
      document.removeEventListener('tts:end', onAudioEnd);
      document.removeEventListener('tts:start', onAudioStart);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // disarm/markDisturbance greifen nur auf Refs zu und sind stabil genug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNative, userId]);

  // Startup-Gates und Touren sind Stoerer — beim Auftauchen UND beim Schliessen.
  useEffect(() => {
    if (!isNative || !userId) return;
    markDisturbance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate, tour.active]);

  // Seitenwechsel: Gespraechsbesuch fuehren, Kaufflaechen als Stoerer, Verlassen als Moment.
  useEffect(() => {
    if (!isNative || !userId) return;
    const t = tracker.current;
    const path = normalizePath(location.pathname);
    const previous = t.previousPath;
    t.previousPath = path;
    if (previous === path) return; // gleiche Seite (Query/State): nichts neu bewerten
    const now = Date.now();
    // Die alte Seite ist hier schon ausgehaengt: eine mit ihr verschwundene Ladeanzeige zaehlt sofort.
    observeDom();
    const purchase = PURCHASE_ROUTE.test(path) || (previous !== null && PURCHASE_ROUTE.test(previous));
    if (purchase) { markDisturbance(); t.lastPurchaseAt = now; }
    const left = t.visit;
    t.visit = CHAT_ROUTE.test(path) ? { userId, path, pending: new Set(), lastReplyAt: null } : null;
    // Ein wartender 'reply'-Moment gehoert zu genau diesem Gespraech; nach Kaeufen oder beim
    // Wiedereintritt in ein Gespraech (der Nutzer spielt weiter) wird nie gefragt.
    if (t.armed?.trigger === 'reply' || purchase || CHAT_ROUTE.test(path)) disarm();
    else if (t.armed) t.armed.since = now; // 'exit': jeder Seitenwechsel startet die Beruhigung neu
    if (!purchase && left && left.userId === userId && left.lastReplyAt !== null && left.pending.size === 0 && !CHAT_ROUTE.test(path)) {
      arm('exit', left, left.lastReplyAt, now);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNative, userId, location.pathname, location.key]);

  return null;
}
